
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { MapPin, Edit, Trash2 } from 'lucide-react';

interface ListingCardProps {
  listing: {
    id: string;
    title: string;
    image: string;
    price?: string;
    location: string;
    breeder: string;
    breed: string;
    listing_type?: string;
    evaluationData?: {
      calvingEase?: string;
      birthWeight?: string;
      weaningWeight?: string;
      milk?: string;
    };
  };
  showEditButton?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ListingCard: React.FC<ListingCardProps> = ({ listing, showEditButton = false, onEdit, onDelete }) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete();
    }
    setIsDeleteDialogOpen(false);
  };

  return (
    <Card className="hover:shadow-lg transition-shadow h-full flex flex-col">
      <Link to={`/listing/${listing.id}`} className="">
        <div className="aspect-[3/2] overflow-hidden rounded-t-lg relative">
          {listing.listing_type && (
            <span className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded z-10">
              {listing.listing_type}
            </span>
          )}
          <img 
            src={listing.image} 
            alt={listing.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </div>
      </Link>
      
      <CardContent className="p-4 space-y-3 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-2">
          <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
            {listing.breed}
          </span>
          {listing.price && (
            <span className="font-bold text-green-700 text-base flex-shrink-0">{listing.price}</span>
          )}
        </div>
        
        <h3 className="font-semibold text-base mb-3 line-clamp-2 leading-tight">{listing.title}</h3>
        
        <div className="flex items-center text-gray-600 mb-3">
          <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
          <span className="text-sm">{listing.location}</span>
        </div>
        
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm text-gray-600 truncate pr-2">{listing.breeder}</span>
        </div>
        
        {/* EPD/EBV Numbers */}
        {listing.evaluationData && (
          <div className="pt-2 border-t border-gray-100 mb-4">
            <div className="grid grid-cols-2 gap-6 text-sm relative">
              {/* Left Column */}
              <div className="space-y-2">
                {listing.evaluationData.calvingEase && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">CE:</span>
                    <span className="font-medium">{listing.evaluationData.calvingEase}</span>
                  </div>
                )}
                {listing.evaluationData.weaningWeight && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">WW:</span>
                    <span className="font-medium">{listing.evaluationData.weaningWeight}</span>
                  </div>
                )}
              </div>
              
              {/* Vertical Separator */}
              <Separator orientation="vertical" className="absolute left-1/2 top-0 h-full transform -translate-x-1/2" />
              
              {/* Right Column */}
              <div className="space-y-2">
                {listing.evaluationData.birthWeight && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">BW:</span>
                    <span className="font-medium">{listing.evaluationData.birthWeight}</span>
                  </div>
                )}
                {listing.evaluationData.milk && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Milk:</span>
                    <span className="font-medium">{listing.evaluationData.milk}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Action Buttons - Always at bottom */}
        {showEditButton && (onEdit || onDelete) && (
          <div className="mt-auto pt-3 border-t border-gray-100">
            <div className="flex gap-2">
              {onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    onEdit();
                  }}
                  className="flex items-center justify-center gap-1 flex-1"
                >
                  <Edit className="w-3 h-3" />
                  Edit
                </Button>
              )}
              {onDelete && (
                <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsDeleteDialogOpen(true);
                      }}
                      className="flex items-center justify-center gap-1 flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Listing</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{listing.title}"? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteConfirm}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ListingCard;
